import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { ADMIN_BTN_GHOST, ADMIN_BTN_PRIMARY, ADMIN_CARD } from './adminTheme';

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'up' || s === 'ok' || s === 'success') return 'border-emerald-400/40 bg-emerald-950/40 text-emerald-100';
  if (s === 'degraded' || s === 'missing_config' || s === 'disabled') {
    return 'border-amber-400/40 bg-amber-950/35 text-amber-100';
  }
  return 'border-rose-400/40 bg-rose-950/35 text-rose-100';
}

/**
 * Aggregated OSINT tool readiness + setup hints (Admin Overview).
 */
export default function AdminToolsHealthPanel() {
  const base = getApiBaseUrl();
  const [tools, setTools] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ts, setTs] = useState('');

  const runTests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${base}/admin/tools-health`, adminFetchInit());
      if (!res.ok) throw new Error(`tools-health HTTP ${res.status}`);
      const data = await res.json();
      setTools(Array.isArray(data?.tools) ? data.tools : []);
      setTs(data?.ts || '');
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    runTests();
  }, [runTests]);

  return (
    <section className={ADMIN_CARD} data-testid="admin-tools-health">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
            Aggregated tools health
          </h2>
          <p className="mt-1 text-[11px] text-slate-400">
            Readiness probes only — no live person/phone/company searches and no result filtering.
            {ts ? <span className="ml-2 font-mono text-slate-500">{ts}</span> : null}
          </p>
        </div>
        <button type="button" onClick={runTests} disabled={loading} className={ADMIN_BTN_PRIMARY}>
          {loading ? 'Testing…' : 'Run tool tests'}
        </button>
      </div>
      {error ? <p className="mb-2 text-[11px] text-rose-300">{error}</p> : null}
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {tools.map((t) => (
          <div key={t.id} className="rounded-xl border border-white/10 bg-slate-950/55 p-3">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-sm font-black text-slate-100">{t.label}</p>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase ${statusTone(t.status)}`}>
                {t.status}
              </span>
            </div>
            <p className="text-[11px] text-slate-400">{t.detail || '—'}</p>
            {t.latency_ms != null ? (
              <p className="mt-1 font-mono text-[10px] text-slate-500">{t.latency_ms} ms</p>
            ) : null}
            {t.setup ? (
              <p className="mt-2 rounded-lg border border-white/5 bg-slate-900/80 px-2 py-1.5 text-[10px] leading-snug text-slate-400">
                <span className="font-bold text-slate-300">Setup: </span>
                {t.setup}
              </p>
            ) : null}
          </div>
        ))}
        {!tools.length && !loading && !error ? (
          <p className="col-span-full py-6 text-center text-sm text-slate-500">No tool probes yet.</p>
        ) : null}
      </div>
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={runTests} className={ADMIN_BTN_GHOST}>
          Refresh
        </button>
      </div>
    </section>
  );
}
